import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { TagIcon } from '@heroicons/react/24/outline'
import { useCandidate } from './CandidateProvider'
import { StatusIndicator } from './ResumeReviewDetail'
import moment from 'moment'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'

export const useServiceRequests = () => {
  const { candidate: { id: candidateId } = {} } = useCandidate()

  const queryPath = `/coach/dashboard_candidates/${candidateId}/service_requests.json`

  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { data, error, isFetching } = useQuery({
    queryKey: ['serviceRequests', candidateId],
    queryFn,
    enabled: !!candidateId,
  })

  if (error) {
    console.error('Error retrieving service requests:', error)
    return null
  }

  const serviceRequests = data?.serviceRequests || []
  return { serviceRequests, isFetching }
}

const ServicesSection = () => {
  const { serviceRequests } = useServiceRequests()
  const { candidate: { programLevel } = {} } = useCandidate()

  return (
    <Table className="w-full" tableClass="rounded-2xl border border-silver">
      <ServicesHeading />
      <TableBody>
        {serviceRequests?.map((serviceRequest, idx) => (
          <ServicesRow
            key={`serviceRequest-${idx}`}
            serviceRequest={serviceRequest}
          />
        ))}
        {(programLevel === 'BRONZE' ||
          programLevel === 'SOLO_PLUS' ||
          programLevel == 'EASE') && (
          <TableRow className="text-center">
            <TableCell colSpan={6} className="text-gray-500">
              There are no one-to-one coaching services for this candidate due
              to their program level. Chat history will appear in Coach Notes.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
export default ServicesSection

const ServicesRow = ({ serviceRequest, ...props }) => {
  const { displayName, createdAt, assignedAt, requestType } =
    serviceRequest || {}
  const requestTypeName = requestType.split('::')[1]
  const displayAssignedAt = assignedAt
    ? moment(assignedAt).format('MM/DD/YYYY')
    : 'N/A'
  const displayCreatedAt = createdAt
    ? moment(createdAt).format('MM/DD/YYYY')
    : 'N/A'

  const linkPath = `#${requestTypeName}/${serviceRequest.id}`

  return (
    <TableRow
      {...props}
      className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]"
    >
      <TableCell>{displayName}</TableCell>
      <TableCell>
        <StatusIndicator serviceRequest={serviceRequest} />
      </TableCell>
      <TableCell>{displayCreatedAt}</TableCell>
      <TableCell>{displayAssignedAt}</TableCell>
      <TableCell>
        <Tags
          multiStep={requestTypeName == 'ResumeReview'}
          oneTime={requestTypeName != 'ResumeReview'}
        />
      </TableCell>
      <TableCell>
        <a href={linkPath} className="cursor-pointer !underline">
          View
        </a>
      </TableCell>
    </TableRow>
  )
}

const Tags = ({ multiStep = false, oneTime = false }) => {
  if (multiStep) {
    return (
      <div className="font-sm text-medium max-w-max rounded-lg bg-[#2383D7] px-4 py-2 text-white">
        <TagIcon className="mr-2 inline h-4" />
        Multi-Step Service
      </div>
    )
  } else if (oneTime) {
    return (
      <div className="font-sm text-medium max-w-max rounded-lg bg-[#D78023] px-4 py-2 text-white">
        <TagIcon className="mr-2 inline h-4" />
        One Time Service
      </div>
    )
  }
}

const ServicesHeading = () => {
  const cells = [
    'Service Type',
    'Status',
    'Date Requested',
    'Date Assigned to Coach',
    'Type',
    'Action',
  ]
  return (
    <TableHeader className="h-6 bg-intoo-blue-medium">
      <TableRow className="text-lg text-white hover:bg-intoo-blue-medium">
        {cells.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}
